import type { Property } from '../types/property';
import type { OpenSchedule } from '../services/openScheduleService';

/**
 * Validate that a property has all required fields for publishing to third-party platforms
 * Returns an array of missing field names, or empty array if all fields are present
 */
export function validateListingFields(
  property: Property | null,
  openSchedule?: OpenSchedule | null
): string[] {
  if (!property) {
    return ['Property data is missing'];
  }

  const missingFields: string[] = [];

  // Validate address fields
  if (!property.address?.street || property.address.street.trim() === '') {
    missingFields.push('property address (street)');
  }
  if (!property.address?.city || property.address.city.trim() === '') {
    missingFields.push('property address (suburb)');
  }
  if (!property.address?.state || property.address.state.trim() === '') {
    missingFields.push('property address (state)');
  }
  if (!property.address?.postalCode || property.address.postalCode.trim() === '') {
    missingFields.push('property address (postcode)');
  }

  // Validate description
  if (!property.description || property.description.trim() === '') {
    if (!property.notes || property.notes.trim() === '') {
      missingFields.push('description');
    }
  }
  
  // Validate features
  if (!property.features?.bedrooms || property.features.bedrooms <= 0) {
    missingFields.push('number of bedrooms');
  }
  if (!property.features?.bathrooms || property.features.bathrooms <= 0) {
    missingFields.push('number of bathrooms');
  }

  // Validate photos - at least one image is required
  const hasImageList = Array.isArray(property.images) && property.images.length > 0;
  const hasImageData = Array.isArray(property.imageData) && property.imageData.length > 0;
  if (!hasImageList && !hasImageData) {
    missingFields.push('photos');
  }
  
  // For sales/auction listings, require a floor plan.
  // For rentals, floor plan is optional.
  const isRentalListing =
    property.type === 'rental' || property.saleType === 'rental';
  if (!isRentalListing) {
    const hasFloorPlan =
      property.floorPlans &&
      property.floorPlans.length > 0 &&
      property.floorPlans.some((fp) => fp.key || fp.url);
    if (!hasFloorPlan) {
      missingFields.push('floor plan');
    }
  }
  
  // For sales/auction listings, require inspection times unless explicitly
  // marked as inspection-by-appointment. For rentals, inspection times
  // are optional and do not block publishing.
  const isByAppointment = (property as any).inspectionByAppointment === true;
  if (!isByAppointment && openSchedule !== undefined && !isRentalListing) {
    const sessions = openSchedule?.sessions || [];
    const hasActiveInspection = sessions.some((session) => (
      session.isActive &&
      session.status === 'scheduled' &&
      !session.isAuction
    ));
    if (!hasActiveInspection) {
      missingFields.push('inspection times');
    }
  }

  // Validate price range - both min and max are required
  if (!property.priceRange || 
      typeof property.priceRange.min !== 'number' || 
      typeof property.priceRange.max !== 'number' ||
      property.priceRange.min <= 0 ||
      property.priceRange.max <= 0) {
    missingFields.push('price range (min and max)');
  }

  return missingFields;
}

/**
 * Get a user-friendly error message for missing listing fields
 */
export function getListingValidationErrorMessage(
  property: Property | null,
  openSchedule?: OpenSchedule | null
): string | null {
  const missingFields = validateListingFields(property, openSchedule);
  
  if (missingFields.length === 0) {
    return null;
  }

  if (missingFields.length === 1) {
    return `Please complete the following before publishing: ${missingFields[0]}.`;
  }

  const lastField = missingFields[missingFields.length - 1];
  const otherFields = missingFields.slice(0, -1).join(', ');
  
  return `Please complete the following before publishing: ${otherFields}, and ${lastField}.`;
}

